import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { UserRoleTypes } from 'features/admin/constants';
import StatusRow from 'features/files/order-insights/components/StatusRow';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { FC, useContext } from 'react';
import { formatDate } from 'utilities/dateFormatter';
import { formatCurrency } from 'utilities/utilities';
import { WireType, WireValidationSubmitResponse } from './interfaces/wireValidation';

interface WireVerificationResultViewProps {
  result: WireValidationSubmitResponse;
  underwriterName?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
  },
  statusContainer: {
    marginBottom: theme.spacing(2),
  },
  heading: {
    paddingLeft: theme.spacing(2),
    fontWeight: 600,
  },
}));

const getWireTypeLabel = (wireType: string): string => {
  const wireTypeLabels: Record<string, string> = {
    [WireType.PERSONAL]: 'Personal',
    [WireType.BUSINESS]: 'Business',
    [WireType.MORTGAGE_PAYOFF]: 'Mortgage Payoff',
  };
  return wireTypeLabels[wireType] || wireType;
};

const WireVerificationResultView: FC<WireVerificationResultViewProps> = ({ result, underwriterName }) => {
  const classes = useStyles();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const isFullyMasked = profileCtx.profile?.roleTypeId === UserRoleTypes.Staff;
  const { wireValidation } = result;

  const wireInfoRows = [
    { label: 'Underwriter:', value: result.underwriterName || underwriterName || result.underwriter },
    { label: 'File Number:', value: result.fileNumber },
    { label: 'Wire Type:', value: getWireTypeLabel(result.wireType) },
    {
      label: 'Wire Submission Date:',
      value: wireValidation.requestedDate ? formatDate(wireValidation.requestedDate, undefined, 'short') : '',
    },
    { label: 'Status:', value: wireValidation.result?.validationStatus, isChip: true },
    { label: 'Message:', value: wireValidation.result?.message },
  ];

  const payeeContactRows = [{ label: 'Name:', value: result.payee }];

  const bankInfoRows = [
    { label: 'Receiving Bank Name:', value: result.bankName },
    { label: 'Wire Amount:', value: formatCurrency(result.wireAmount) },
    {
      label: 'Routing Number:',
      value: result.routingNumber,
      masked: true,
      dataTestId: 'wire-validation-button-toggle-routing-number',
    },
    {
      label: 'Account Number:',
      value: result.accountNumber,
      masked: true,
      dataTestId: 'wire-validation-button-toggle-account-number',
    },
  ];

  return (
    <Box className={classes.root} data-testid="wire-validation-result-view">
      <div className={classes.statusContainer}>
        {wireInfoRows.map((item, idx) => (
          <StatusRow key={item.label} index={idx} label={item.label} value={item.value} isPlainText={!item.isChip} />
        ))}
      </div>

      {result.wireType !== WireType.MORTGAGE_PAYOFF && (
        <div className={classes.statusContainer}>
          <Typography variant="h4" className={classes.heading}>
            Payee Contact
          </Typography>
          {payeeContactRows.map((item, idx) => (
            <StatusRow key={item.label} index={idx} label={item.label} value={item.value} isPlainText />
          ))}
        </div>
      )}

      <div className={classes.statusContainer}>
        <Typography variant="h4" className={classes.heading}>
          Bank Information
        </Typography>
        {bankInfoRows.map((item, idx) => (
          <StatusRow
            key={item.label}
            index={idx}
            label={item.label}
            value={item.value}
            isPlainText
            masked={item.masked}
            fullyMasked={isFullyMasked}
            dataTestId={item.dataTestId}
          />
        ))}
      </div>
    </Box>
  );
};

export default WireVerificationResultView;
